import { SafeBaseObject } from "@app/core/models/base-object.models";
import { lastValueFrom, Observable } from "rxjs";
import { DateTime } from "luxon";

export interface IResApiGeoTreeParameters {
  Level1ID?: number;
  Level2ID?: number;
  Level3ID?: number;
  Level4ID?: number;
  Level5ID?: number;
  DestinationTypeID: string;
  DepartmentID: number;
  OrderType?: number;
  Setup?: number;
}

/** Duck-type for geo tree navigate (RescomApiService / ResadmService). */
export interface IGeoTreeNavigateApi {
  geoTreeNavigate(filters: IResApiGeoTreeParameters, orderType?: number): Observable<GeoTreeDestination[]>;
}

export class GeoTreeDestination extends SafeBaseObject {
  geoTreeID = 0
  geoTreeName = ''
  geoTreeNameHtml = ''
  geoTreeTypeID = 0
  destinationTypeID = 0
  parentID: number[] = []

  firstParentID?: number;
  externalCityID = 0

  onLoading = false;
  isLeaf = false;

  children: GeoTreeDestination[] = []
  expanded?: boolean;
  isVisible?: boolean;

  constructor(data?: any) {
    super();
    if (data) {
      super.updateData(data);
      this.updateData(data);
    }
  }

  updateData(data?: any) {
    if (!data) return;
    if (data.LEVEL_1_ID) this.parentID.push(data.LEVEL_1_ID)
    if (data.LEVEL_2_ID) this.parentID.push(data.LEVEL_2_ID)
    if (data.LEVEL_3_ID) this.parentID.push(data.LEVEL_3_ID)
    if (data.LEVEL_4_ID) this.parentID.push(data.LEVEL_4_ID)
    if (data.LEVEL_5_ID) this.parentID.push(data.LEVEL_5_ID)
    if (this.parentID.length > 0) this.firstParentID = this.parentID[this.parentID.length - 1]
    this.geoTreeNameHtml = this.geoTreeName
  }

  static load(api: IGeoTreeNavigateApi, isGeoSearchPage: boolean = false, orderType: number = 0, departmentID = 1): Observable<GeoTreeDestination[]> {
    const isGeoSetupParam = isGeoSearchPage ? 1 : 0
    const prms: IResApiGeoTreeParameters = {
      DestinationTypeID: '1',
      DepartmentID: departmentID,
      Setup: isGeoSetupParam
    }
    return api.geoTreeNavigate(prms, orderType);
  }
  static rootPayload(departmentID = 1) {
    return {
      DestinationTypeID: '1',
      DepartmentID: departmentID,
    } as IResApiGeoTreeParameters
  }
  static loadRoot(api: IGeoTreeNavigateApi, isGeoSearchPage: boolean = false, orderType: number = 0, departmentID = 1): Promise<GeoTreeDestination[]> {

    return lastValueFrom(api.geoTreeNavigate({ ...GeoTreeDestination.rootPayload(departmentID), Setup: isGeoSearchPage ? 1 : undefined }, orderType));
  }
  childrenPayload(departmentID = 1) {
    const params: any = {
      DestinationTypeID: (this.destinationTypeID + 1).toString(),
      DepartmentID: departmentID,
    }
    this.parentID.forEach((id, idx) => {
      params[`Level${idx + 1}ID`] = id;
    })
    params[`Level${this.destinationTypeID}ID`] = this.geoTreeID;
    return params as IResApiGeoTreeParameters
  }

  loadChildrenPromise(api: IGeoTreeNavigateApi, orderType: number = 0, departmentID = 1) {
    return lastValueFrom(api.geoTreeNavigate({ ...this.childrenPayload(departmentID) }, orderType));
  }

  loadChildren(api: IGeoTreeNavigateApi) {
    this.onLoading = true;
    api.geoTreeNavigate(this.childrenPayload()).subscribe({
      next: (nodes) => {
        this.children = nodes;
        this.onLoading = false;
      },
      error: (_) => this.onLoading = false
    })
  }
  hightlightSearchText(text: string) {
    this.geoTreeNameHtml = this.geoTreeName.replace(new RegExp(text, 'gi'), `<span class="fw-bold fst-italic">${text}</span>`);
  }
}
export class ReservationApiGeotreeType extends SafeBaseObject {
  destinationTypeID?: number;
  destinationTypeName?: string;

  constructor(data?: any) {
    super();
    if (data) {
      this.updateData(data);
    }
  }
}

export interface IReservationApiGeotreeMovement {
  TypeOrder?: number;
  Source?: number;
  Target?: number;
  Option?: number;
}
export class ResGeoTreeImageCategory extends SafeBaseObject {
  docCatID: number = 0;
  categoryName: string = '';

  constructor(data?: any) {
    super();
    if (data) {
      super.updateData(data);
      this.updateData(data);
    }
  }
  updateData(data?: any) {
    if (!data) return;
  }
}
export class ReservationApiGeotree extends SafeBaseObject {
  geoTreeID = 0
  geoTreeName?: string;
  geoTreeTypeID = 0
  destinationTypeID?: number;
  departmentID?: number;
  departmentName?: string;
  level1ID?: number;
  level2ID?: number;
  level3ID?: number;
  level4ID?: number;
  level5ID?: number;
  portCod?: string;
  cityCod?: string;
  mainIataCod?: string;
  latitude?: number;
  longitude?: number;
  externalCityID?: number;
  externalCountryID?: number;
  companyID?: number;
  salesPriority?: number;
  destinationPriority?: number;
  status?: string;
  images: ReservationApiGeotreeImage[] = [];
  title?: string;
  description?: string;
  descriptionAI?: string;
  isValid = false;
  isVisibleDest = false;
  isVisibleSales = false;
  bestTimeToGo?: number[] = [];
  timeZone?: number[] = [];
  breadcrumbs?: string = '';
  weatherInfo = ''
  weatherInfoParsed: any = {}
  documents: ResGeoTreeDocument[] = [];
  supplierUniqueID?: number;
  aKRepresentativeID?: number;

  constructor(data?: any) {
    super();
    if (data) {
      this.updateData(data);
    }
  }

  override updateData(data?: any): void {
    if (data) {
      super.updateData(data);
      this.addProperty('level1ID', data, 'LEVEL_1_ID');
      this.addProperty('level2ID', data, 'LEVEL_2_ID');
      this.addProperty('level3ID', data, 'LEVEL_3_ID');
      this.addProperty('level4ID', data, 'LEVEL_4_ID');
      this.addProperty('level5ID', data, 'LEVEL_5_ID');
      this.bestTimeToGo = data.BestTimeToGo ? data.BestTimeToGo.split(',').map(Number) : [];
      this.timeZone = data.TimeZone ? data.TimeZone.split(',').map(Number) : [];

      this.isValid = this.geoTreeID > 0;
      if (data.Images) {
        this.images = data.Images.map((i: any) => new ReservationApiGeotreeImage(i));
      }
      if (data.Documents) {
        this.documents = data.Documents.map((d: any) => new ResGeoTreeDocument(d));
      }

      if (this.weatherInfo !== '') {
        try {
          this.weatherInfoParsed = JSON.parse(this.weatherInfo)
        }
        catch {
          this.weatherInfoParsed = {}
        }
      }
    }
  }
}
export class ReservationApiGeotreeImage extends SafeBaseObject {
  geoTreeIMGID?: number;
  geoTreeID?: number;
  fileID?: number;
  fileGUID?: string;
  FileGUID?: string;
  fileName?: string;
  status?: string = 'A'
  geoTreeIMGType?: string;
  geoTreeIMGTypeID?: number = 1;
  categoryName?: string;
  docCatID?: number;
  imgNote?: string;

  constructor(data?: any) {
    super();
    if (data) {
      this.updateData(data);
    }
  }
}

export class ResGeoTreeDocument extends SafeBaseObject {
  geoTreeDocID = 0
  geoTreeID = 0
  fileID = 0
  fileGUID = ''
  fileName = ''
  documentTypeID = 0
  documentType = ''
  documentUsageID = 0
  documentUsage = ''
  validityStartDate = DateTime.invalid('Empty')
  validityEndDate = DateTime.invalid('Empty')
  note = ''
  status = 'A'

  constructor(data?: any) {
    super();
    if (data) {
      this.updateData(data);
    }
  }
}

export class ResGeoTreeDocumentType extends SafeBaseObject {
  documentTypeID = 0
  documentType = ''

  constructor(data?: any) {
    super();
    if (data) {
      this.updateData(data);
    }
  }
}

export class ResGeoTreeDocumentUsage extends SafeBaseObject {
  documentUsageID = 0
  documentUsage = ''

  constructor(data?: any) {
    super();
    if (data) {
      this.updateData(data);
    }
  }
}

export interface IResApiGetCityTax {
  CityID?: number
  StartDate?: DateTime
  EndDate?: DateTime
  Currency?: string
  Star?: string
}

export class ResAdmCityTax extends SafeBaseObject {
  cityID = 0
  city = ''
  countryID = 0
  country = ''
  cityDetails: ResAdmCityTaxDetail[] = []

  currenciesSaved: string[] = []

  constructor(data?: any) {
    super();
    if (data) {
      this.updateData(data);
      if (data?.CityDetails) {
        this.cityDetails = data.CityDetails.map((detail: any) => new ResAdmCityTaxDetail(detail));
        this.currenciesSaved = [...new Set(this.cityDetails.map((detail: ResAdmCityTaxDetail) => detail.currency))];
      }

    }
  }
}

export class ResAdmCityTaxDetail extends SafeBaseObject {
  cityTaxID = 0
  cityID = 0
  priceSetupID = 0
  priceSetup = ''
  startDate = DateTime.invalid('Empty')
  endDate = DateTime.invalid('Empty')
  percentage?: number
  fixedAmount?: number
  currency = ''
  star = ''
  note = ''
  status = 'A'

  constructor(data?: any) {
    super();
    if (data) {
      this.updateData(data);
    }
  }
}

export interface IResApiManageCityTax {
  Tax: IResApiManageCityTaxSingle[]
}

export interface IResApiManageCityTaxSingle {
  CityTaxID?: number
  CityID: number
  PriceSetupID?: number
  StartDate: DateTime
  EndDate: DateTime
  Percentage?: number
  FixedAmount?: number
  Currency: string
  Star?: string
  Note?: string
  Status: string
}

export interface IResApiGetServiceTax {
  CityID?: number
  GeoTreeID?: number
  GeoTreeName?: string
  StartDate?: DateTime
  EndDate?: DateTime
  Currency?: string
}
export class ResAdmServiceTax extends SafeBaseObject {
  geoTreeID = 0
  geoTreeName = ''
  destinationTypeID = 0
  geoTreeName_1 = ''
  geoTreeName_2 = ''
  geoTreeName_3 = ''
  status = 'A'
  details: ResAdmServiceTaxDetail[] = []

  constructor(data?: any) {
    super();
    if (data) {
      this.updateData(data);
      if (data?.Details) {
        this.details = data.Details.map((detail: any) => new ResAdmServiceTaxDetail(detail));
      }
    }
  }
}

export class ResAdmServiceTaxDetail extends SafeBaseObject {
  serviceTaxID = 0
  geoTreeID = 0
  priceSetupID = 0
  priceSetup = ''
  startDate = DateTime.invalid('Empty')
  endDate = DateTime.invalid('Empty')
  percentage = 0
  fixedAmount = 0
  currency = ''
  note = ''
  status = 'A'

  constructor(data?: any) {
    super();
    if (data) {
      this.updateData(data);
    }
  }
}

export interface IResApiManageServiceTax {
  Tax: IResApiManageServiceTaxSingle[]
}
export interface IResApiManageServiceTaxSingle {
  ServiceTaxID?: number
  GeotreeID?: number
  PriceSetupID?: number
  StartDate?: DateTime
  EndDate?: DateTime
  Percentage?: number
  FixedAmount?: number
  Currency?: string
  Note?: string
  Status?: string
}

export interface IResApiGetVATSetup {
  CityID?: number
  GeoTreeID?: number
  GeoTreeName?: string
  StartDate?: DateTime
  EndDate?: DateTime
  Currency?: string
}

export class ResAdmVATSetup extends SafeBaseObject {
  geoTreeID = 0
  geoTreeName = ''
  destinationTypeID = 0
  geoTreeName_1 = ''
  geoTreeName_2 = ''
  geoTreeName_3 = ''
  details: ResAdmVATSetupDetail[] = []
  status = 'A'

  constructor(data?: any) {
    super();
    if (data) {
      this.updateData(data);
      if (data?.Details) {
        this.details = data.Details.map((detail: any) => new ResAdmVATSetupDetail(detail));
      }
    }
  }
}
export class ResAdmVATSetupDetail extends SafeBaseObject {
  vatSetupID = 0
  geoTreeID = 0
  priceSetupID = 0
  priceSetup = ''
  startDate = DateTime.invalid('Empty')
  endDate = DateTime.invalid('Empty')
  percentage = 0
  fixedAmount = 0
  currency = ''
  note = ''
  status = 'A'

  constructor(data?: any) {
    super();
    if (data) {
      this.updateData(data);
    }
  }
}

export interface IResApiManageVATSetup {
  Tax: IResApiManageVATSetupSingle[]
}
export interface IResApiManageVATSetupSingle {
  VATSetupID?: number
  GeotreeID?: number
  PriceSetupID?: number
  StartDate?: DateTime
  EndDate?: DateTime
  Percentage?: number
  FixedAmount?: number
  Currency?: string
  Note?: string
  Status?: string
}

export interface IResApiGetHolidaySetup {
  CityID?: number
  GeoTreeID?: number
  GeoTreeName?: string
  HolidayDate?: DateTime
}

export class ResAdmHolidaySetup extends SafeBaseObject {
  geoTreeID = 0
  geoTreeName = ''
  destinationTypeID = 0
  geoTreeName_1 = ''
  geoTreeName_2 = ''
  geoTreeName_3 = ''
  details: ResAdmHolidaySetupDetail[] = []
  status = 'A'

  constructor(data?: any) {
    super();
    if (data) {
      this.updateData(data);
      if (data?.Details) {
        this.details = data.Details.map((detail: any) => new ResAdmHolidaySetupDetail(detail));
      }
    }
  }
}

export class ResAdmHolidaySetupDetail extends SafeBaseObject {
  holidayID = 0
  holidayName = ''
  holidayDate = DateTime.invalid('Empty')
  companyID = 0
  geoTreeID = 0
  note = ''
  status = 'A'

  constructor(data?: any) {
    super();
    if (data) {
      this.updateData(data);
    }
  }
}

export interface IResApiManageHolidaySetup {
  Holiday: IResApiManageHolidaySetupSingle[]
}
export interface IResApiManageHolidaySetupSingle {
  HolidayID?: number
  HolidayName?: string
  HolidayDate?: DateTime
  CompanyID?: number
  GeoTreeID?: number
  Note?: string
  Status?: string
}

export class ResAdmEcmGeoTreeReport extends SafeBaseObject {
  geoTreeID = 0
  geoTreeName = ''
  destinationTypeID = 0
  description = ''
  parentGeoTreeID?: number
  parentGeoTreeName = ''
  geoTreeIMGID?: number
  fileID?: number
  fileGUID = ''
  cityID?: number
  masterCity = ''

  constructor(data?: any) {
    super();
    if (data) {
      this.updateData(data);
    }
  }
}